/**
 * Who a Pictures surface works for (spec 030 D-12): a story (026, 027) or a
 * standalone scene with its own picture map and look. The Pictures view,
 * the Generate panel, the Look row, the style picker and the discard flow
 * read the owner instead of the story store. No provider mounted means the
 * story owner, so the story editor behaves exactly as before.
 *
 * `ownerActions(ref)` is the same for code outside React (`imageGenStore`).
 */
import React, { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { PictureMap, SlotUse } from '../../../../Shared/src/@types';
import type { ImageOwnerRef } from '../../../../Shared/src/images';
import { SCENE_PICTURE_KEY, scenePictureMapOf, type OperationStep, type SceneDocument } from '../../../../Shared/src/operations';
import { useSceneStore } from '../../stores/sceneStore';
import { useStoryStore } from '../../stores/storyStore';
import { picturesSearch, type PicturesQuery } from './usePicturesNav';
import type { PictureSceneRow } from './picturesModel';

export type PictureNoun = 'story' | 'scene';

/** A picture change's outcome: the story store's shape. */
export type PictureApplyResult =
  | { ok: true }
  | { ok: false; reason: 'failed' | 'network'; errors: { code: string; message: string; path?: string }[] };

export type PictureFillResult =
  | { ok: true; places: number; label: string }
  | { ok: false; reason: 'failed' | 'network'; errors: { code: string; message: string; path?: string }[] };

export interface PictureOwner {
  ref: ImageOwnerRef;
  noun: PictureNoun;
  /** Derived and live: a story's from the story store, a scene's from the open scene (unsaved edits count). */
  map: PictureMap;
  /** The scene owner's one scene (keyed `scene`); a story's surfaces read the story's scenes as before. */
  scenes: PictureSceneRow[];
  /** One history entry: the story's, or the scene's (then saved at once, D-5). */
  apply(steps: OperationStep[]): Promise<PictureApplyResult>;
  fillPicture(slotId: string, mediaId: number, opts?: { uses?: SlotUse[] }): Promise<PictureFillResult>;
  discardPictures(mediaIds: number[]): Promise<PictureApplyResult>;
  undo(): void;
  redo(): void;
  /** Saves dirty content before a route reads the saved owner. */
  saveFirst(): Promise<void>;
  picturesHref(query?: PicturesQuery): string;
  /** A scene's stories (D-10), when known. */
  storyLinks?: { storyId: number; storyName: string }[];
}

/** The owner's actions, outside React. */
export interface OwnerActions {
  map(): PictureMap;
  apply(steps: OperationStep[]): Promise<PictureApplyResult>;
  fillPicture(slotId: string, mediaId: number, opts?: { uses?: SlotUse[] }): Promise<PictureFillResult>;
  discardPictures(mediaIds: number[]): Promise<PictureApplyResult>;
  undo(): Promise<void> | void;
  saveFirst(): Promise<void>;
}

const saveDirtyScene = async () => {
  const sc = useSceneStore.getState?.();
  if (sc?.scene && sc.isDirty) await sc.saveScene();
};

const sceneApply = async (steps: OperationStep[]): Promise<PictureApplyResult> => {
  const result = useSceneStore.getState().applyOperations(steps);
  if (result.ok === false) return { ok: false, reason: 'failed', errors: result.errors };
  await useSceneStore.getState().saveScene();
  return { ok: true };
};

const sceneMapNow = (): PictureMap => {
  const scene = useSceneStore.getState().scene;
  return scene ? scenePictureMapOf(scene as SceneDocument) : { slots: [] };
};

/** The actions of a story or scene owner (the scene owner acts on the scene store's open scene). */
export function ownerActions(ref: Pick<ImageOwnerRef, 'kind'> | undefined): OwnerActions {
  if (ref?.kind === 'scene') {
    return {
      map: sceneMapNow,
      apply: sceneApply,
      fillPicture: (slotId, mediaId, opts) => useSceneStore.getState().fillPicture(slotId, mediaId, opts),
      discardPictures: (mediaIds) => useSceneStore.getState().discardPictures(mediaIds),
      undo: () => useSceneStore.getState().undo(),
      saveFirst: saveDirtyScene,
    };
  }
  return {
    map: () => useStoryStore.getState().pictureMap,
    apply: (steps) => useStoryStore.getState().applyOperations(steps),
    fillPicture: (slotId, mediaId, opts) => useStoryStore.getState().fillPicture(slotId, mediaId, opts),
    discardPictures: (mediaIds) => useStoryStore.getState().discardPictures(mediaIds),
    undo: () => useStoryStore.getState().undo(),
    saveFirst: saveDirtyScene,
  };
}

/** `null` is the story owner (no provider mounted). */
export const PictureOwnerContext: React.Context<PictureOwner | null> = createContext<PictureOwner | null>(null);

const EMPTY_SCENES: PictureSceneRow[] = [];

/** The story owner: today's behaviour, from the story store. */
export function useStoryPictureOwner(): PictureOwner {
  const storyId = useStoryStore((s) => s.story?.id);
  const map = useStoryStore((s) => s.pictureMap);
  return useMemo<PictureOwner>(() => {
    const actions = ownerActions({ kind: 'story' });
    return {
      ref: { kind: 'story', id: storyId ?? 0 },
      noun: 'story',
      map,
      scenes: EMPTY_SCENES,
      apply: actions.apply,
      fillPicture: actions.fillPicture,
      discardPictures: actions.discardPictures,
      undo: () => { void useStoryStore.getState().undo(); },
      redo: () => { void useStoryStore.getState().redo(); },
      saveFirst: actions.saveFirst,
      picturesHref: (q) => `/story/${storyId ?? ''}${picturesSearch(q)}`,
    };
  }, [storyId, map]);
}

/** The scene owner (spec 030): the scene store's open scene, its own map and its stories. */
export function useScenePictureOwner(storyLinks?: { storyId: number; storyName: string }[]): PictureOwner {
  const scene = useSceneStore((s) => s.scene);
  return useMemo<PictureOwner>(() => {
    const id = scene?.id ?? 0;
    const actions = ownerActions({ kind: 'scene' });
    const doc = (scene ?? { name: '', sprites: [], dialogs: [] }) as SceneDocument;
    const name = scene?.name ?? '';
    return {
      ref: { kind: 'scene', id },
      noun: 'scene',
      map: scene ? scenePictureMapOf(doc) : { slots: [] },
      scenes: scene
        ? [{ key: SCENE_PICTURE_KEY, name, storySceneId: 0, sceneId: id, doc: doc as PictureSceneRow['doc'] }]
        : EMPTY_SCENES,
      apply: actions.apply,
      fillPicture: actions.fillPicture,
      discardPictures: actions.discardPictures,
      undo: () => useSceneStore.getState().undo(),
      redo: () => useSceneStore.getState().redo(),
      saveFirst: actions.saveFirst,
      picturesHref: (q) => `/scene/${id}${picturesSearch(q)}`,
      ...(storyLinks ? { storyLinks } : {}),
    };
  }, [scene, storyLinks]);
}

/** The owner in reach: the provider's, else the story owner. */
export function usePictureOwner(): PictureOwner {
  const provided = useContext(PictureOwnerContext);
  const story = useStoryPictureOwner();
  return provided ?? story;
}

/** Mounts the scene owner for the scene store's open scene (the standalone scene editor, spec 030). */
export const ScenePictureOwnerProvider: React.FC<{ children: ReactNode; storyLinks?: { storyId: number; storyName: string }[] }> = ({ children, storyLinks }) => {
  const owner = useScenePictureOwner(storyLinks);
  return <PictureOwnerContext.Provider value={owner}>{children}</PictureOwnerContext.Provider>;
};

/** "story" or "scene" in a sentence: `noun` replaced where the story's words say "story". */
export const withNoun = (text: string, noun: PictureNoun): string =>
  (noun === 'scene' ? text.replace(/\bstory\b/g, 'scene').replace(/\bStory\b/g, 'Scene') : text);
